import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { usePageMeta } from "@/hooks/use-page-meta";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "Clients don't show up and I lose money. How do I reduce no-shows?",
    answer:
      "Buukd cuts no-shows with automated email and SMS reminders (e.g. 24 hours and 2 hours before), so clients are reminded without you calling. You can require a deposit at booking so a no-show costs you less. One calendar shows who's coming when, and you can see visit history so you know who tends to cancel or not show.",
  },
  {
    question: "Bookings get lost in WhatsApp and I end up double-booking. How do I fix this?",
    answer:
      "Buukd gives you one public booking page and one calendar. Clients pick an available time; that slot is blocked automatically. No more scrolling chats, copying names into a notebook, or juggling multiple calendars. Staff and you see the same schedule, so double-bookings stop.",
  },
  {
    question: "I spend hours calling or texting to confirm appointments. Is there a better way?",
    answer:
      "Yes. With Buukd, clients book online 24/7 and get automatic confirmations. Reminders go out 24 hours and 2 hours before (and you get a 2-hour reminder too). Clients can reschedule or cancel from their confirmation link. You get in-app alerts for new bookings instead of chasing messages.",
  },
  {
    question: "I don't know who my regulars are or what they've paid. How can I keep track?",
    answer:
      "Buukd keeps a shared customer list for your business. Every appointment and payment is linked to that customer. You see visit history, what they booked, what they paid, and whether they've no-showed. One profile per client across appointments, memberships, and orders.",
  },
  {
    question: "Collecting deposits and remaining payments is messy. How does Buukd handle payments?",
    answer:
      "You can turn on deposit collection so clients pay a deposit when they book (via Paystack). Optionally, let them pay the full amount at booking. For the rest, send a 'Pay remaining balance' link so they settle before or at the visit. You set payment instructions in one place; Buukd respects coupons and discounts so the remaining balance is correct.",
  },
  {
    question: "My staff and I use different notebooks or sheets. How do we stay on the same page?",
    answer:
      "Buukd is one dashboard for appointments, members, orders, and events. Everyone with access sees the same calendar, customer list, and orders. The Access Desk lets staff check members in and out and sell day passes. The Log Book keeps daily records in one place instead of scattered sheets.",
  },
  {
    question: "Catering and event orders get lost in DMs. How do I capture them properly?",
    answer:
      "Buukd offers a public orders page with a cart that runs 24/7, and event or buffet request forms so every detail is captured in one place. Orders and requests land in your dashboard—no more scrolling chats or lost DMs. You can share one link for menus and promos on WhatsApp, email, or social.",
  },
  {
    question: "Members and walk-ins are hard to track at the door. What can I use?",
    answer:
      "Buukd's membership module includes plans with clear start and end dates, and member IDs (e.g. TSC-000123) so check-ins are fast. The Access Desk is a POS-style screen where staff search members, check them in/out, and sell day passes or tickets. It's mobile-friendly so your team can use it at the door.",
  },
  {
    question: "I can't fill slow days or bring back past clients. How do I reach them?",
    answer:
      "Buukd keeps your customer list with visit and booking history. You can run simple email or SMS campaigns to target segments—for example, people who haven't visited in months or who booked a specific service. Promote offers, slow-day discounts, or new services without manually going through contacts.",
  },
  {
    question: "I need invoices and professional quotes for B2B. Does Buukd do that?",
    answer:
      "Yes. Buukd's invoicing module lets you create and send invoices with taxes and discounts. Each invoice has a public page so clients can view and pay. You also get events with capacity limits and paid tickets (with QR codes), so one platform covers appointments, memberships, orders, events, and invoicing as you grow.",
  },
];

export default function FAQ() {
  usePageMeta({
    title: "FAQ – Buukd",
    description:
      "Frequently asked questions about Buukd: reduce no-shows, stop double-bookings, collect deposits, manage members, and grow your service business. Ghana, Nigeria, Africa, UK, USA.",
    path: "/faq",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />

      <main className="flex-1 pt-24 pb-12">
        <div className="container-app max-w-3xl">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Frequently Asked Questions
          </h1>
          <p className="text-muted-foreground mb-10">
            Real pressing issues for service businesses — and how Buukd solves them.
          </p>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-semibold text-foreground">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
